import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import DeathVerificationOutputComponent from '../output/DeathVerificationOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const DeathVerificationInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ serialNumber: '', stateName: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleField = (field, transform = (v) => v) => (e) => {
        const value = transform(e.target.value || '');
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.serialNumber.trim()) next.serialNumber = 'Serial number is required';
        if (!formData.stateName.trim()) next.stateName = 'State name is required';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/death/verification', {
                serial_number: formData.serialNumber.trim(),
                state_name: formData.stateName.trim().toLowerCase(),
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Verification failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Death record verified', detail: 'Report ready below', life: 2000 });
            // Credits just changed; drop the cached balance so every consumer updates.
            invalidateReportService(serviceId);
        } catch (error) {
            // Surepass returned partial data even on failure — display it
            const errBody = error.response?.data;
            if (errBody?.data != null) {
                setReportData(errBody);
                toast.current?.show({ severity: 'warn', summary: 'Verification result', detail: errBody.message || 'Result shown below', life: 4000 });
                // Credits just changed; drop the cached balance so every consumer updates.
                invalidateReportService(serviceId);
                return;
            }
            console.error('Death verification failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const responseData = error.response?.data;
            let detail;
            if (status === 402) {
                const c = responseData || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            } else if (status === 429) {
                detail = 'Too many requests. Please wait a minute before trying again.';
            } else {
                detail = responseData?.message || error.message || 'Verification failed';
            }
            toast.current?.show({ severity: 'error', summary: 'Verification failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Serial Number:</span> <span className="text-900 font-medium">{formData.serialNumber}</span></div>
                    <div className="mb-2"><span className="text-600">State:</span> <span className="text-900 font-medium">{formData.stateName}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm death verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ serialNumber: '', stateName: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-file"
                        title="Death Verification"
                        subtitle="Verify a death record by application number and state via Surepass"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-5">
                                        <label htmlFor="serialNo" className="block font-medium text-sm mb-2">
                                            Serial Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-file" />
                                            <InputText
                                                id="serialNo"
                                                value={formData.serialNumber}
                                                onChange={handleField('serialNumber', (v) => v.trim())}
                                                placeholder="e.g. ES4001234"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.serialNumber })}
                                                aria-describedby="serialNo-help"
                                                aria-invalid={!!errors.serialNumber}
                                            />
                                        </IconField>
                                        {errors.serialNumber && (
                                            <small id="serialNo-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.serialNumber}
                                            </small>
                                        )}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="state" className="block font-medium text-sm mb-2">
                                            State Name <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-map-marker" />
                                            <InputText
                                                id="state"
                                                value={formData.stateName}
                                                onChange={handleField('stateName')}
                                                placeholder="e.g. Maharashtra"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.stateName })}
                                                aria-describedby="state-help"
                                                aria-invalid={!!errors.stateName}
                                            />
                                        </IconField>
                                        {errors.stateName && (
                                            <small id="state-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.stateName}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="death-verification"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Verify Record"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Verify another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <DeathVerificationOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default DeathVerificationInputComponent;
